import React from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { useTheme } from '../../context/ThemeContext';
import { HiOutlineChartBarSquare, HiOutlineArrowTrendingUp } from 'react-icons/hi2';
import styles from './Analytics.module.css';

const Analytics = ({ analytics }) => {
  const { isDark } = useTheme();

  if (!analytics) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner}></div>
        <p>Loading analytics...</p>
      </div>
    );
  }

  const chartColors = {
    line: isDark ? '#58a6ff' : '#0969da',
    bar: isDark ? '#58a6ff' : '#0969da',
    grid: isDark ? '#30363d' : '#d1d9e0',
    text: isDark ? '#8b949e' : '#656d76',
    background: isDark ? '#161b22' : '#ffffff'
  };

  const formatTimelineData = (data) => {
    return data.map(item => ({
      date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      applications: parseInt(item.count)
    }));
  };

  const formatPlatformData = (data) => {
    return data.map(item => ({
      platform: item.platform,
      count: parseInt(item.count)
    }));
  };

  const timelineData = formatTimelineData(analytics.timeline || []);
  const platformData = formatPlatformData(analytics.platforms || []);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className={styles.tooltip}>
          <p className={styles.tooltipLabel}>{label}</p>
          <p className={styles.tooltipValue}>
            {payload[0].value} {payload[0].value === 1 ? 'application' : 'applications'}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className={styles.analytics}>
      <div className={styles.chartsGrid}>
        <div className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <div className={styles.chartTitleSection}>
              <HiOutlineArrowTrendingUp className={styles.chartIcon} />
              <div>
                <h3 className={styles.chartTitle}>Applications Over Time</h3>
                <p className={styles.chartSubtitle}>Last 30 days activity</p>
              </div>
            </div>
          </div>
          <div className={styles.chartContainer}>
            {timelineData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={timelineData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fill: chartColors.text, fontSize: 12 }}
                    axisLine={{ stroke: chartColors.grid }}
                    tickLine={{ stroke: chartColors.grid }}
                  />
                  <YAxis 
                    tick={{ fill: chartColors.text, fontSize: 12 }}
                    axisLine={{ stroke: chartColors.grid }}
                    tickLine={{ stroke: chartColors.grid }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Line 
                    type="monotone" 
                    dataKey="applications" 
                    stroke={chartColors.line}
                    strokeWidth={3}
                    dot={{ fill: chartColors.line, strokeWidth: 2, r: 5 }}
                    activeDot={{ r: 7, fill: chartColors.line, strokeWidth: 2, stroke: chartColors.background }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className={styles.noData}>
                <HiOutlineArrowTrendingUp className={styles.noDataIcon} />
                <p>No application data available for the last 30 days</p>
                <span className={styles.noDataHint}>Start adding applications to see trends</span>
              </div>
            )}
          </div>
        </div>

        <div className={styles.chartCard}>
          <div className={styles.chartHeader}>
            <div className={styles.chartTitleSection}>
              <HiOutlineChartBarSquare className={styles.chartIcon} />
              <div>
                <h3 className={styles.chartTitle}>Applications by Platform</h3>
                <p className={styles.chartSubtitle}>Distribution across job platforms</p>
              </div>
            </div>
          </div>
          <div className={styles.chartContainer}>
            {platformData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={platformData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                  <XAxis 
                    dataKey="platform" 
                    tick={{ fill: chartColors.text, fontSize: 11 }}
                    axisLine={{ stroke: chartColors.grid }}
                    tickLine={{ stroke: chartColors.grid }}
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis 
                    tick={{ fill: chartColors.text, fontSize: 12 }}
                    axisLine={{ stroke: chartColors.grid }}
                    tickLine={{ stroke: chartColors.grid }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar 
                    dataKey="count" 
                    fill={chartColors.bar}
                    radius={[6, 6, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className={styles.noData}>
                <HiOutlineChartBarSquare className={styles.noDataIcon} />
                <p>No platform data available</p>
                <span className={styles.noDataHint}>Add applications to see platform distribution</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;